import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, AnimatePresence } from 'framer-motion';
import AppleStudioTrophy from './AppleTrophy3D';

function DynamicAwardCard({ item, index, onImageClick }) {
  const isLeft = index % 2 === 0;

  return (
    <div
      className={`d-flex align-items-center px-3 px-md-5 my-5 w-100 ${
        isLeft ? 'justify-content-start' : 'justify-content-end'
      }`}
    >
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: false, amount: 0.25 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{
          maxWidth: '480px',
          width: '100%',
          background: 'rgba(7, 10, 19, 0.92)',
          border: isLeft ? '1px solid rgba(0, 242, 254, 0.4)' : '1px solid rgba(245, 158, 11, 0.4)',
          borderRadius: '1.25rem',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(12px)',
        }}
        className="p-4"
      >
        <span className="badge bg-warning text-dark fw-bold px-3 py-1 mb-2">
          {item.year}
        </span>
        <p className="fw-bold text-info mb-1" style={{ fontSize: '0.85rem', letterSpacing: '0.08em' }}>
          {item.category}
        </p>

        {/* Vertically Stacked Image Proofs (Each on its own line) */}
        {item.images && item.images.length > 0 && (
          <div className="container p-0 my-3">
            <div className="row g-3">
              {item.images.map((imgSrc, i) => {
                const fullSrc = imgSrc.startsWith('http') ? imgSrc : `https://sanjaydhir.com/api${imgSrc}`;
                return (
                  <div key={i} className="col-12">
                    <div
                      onClick={() => onImageClick(fullSrc, item.title)}
                      className="position-relative overflow-hidden rounded border border-secondary border-opacity-50"
                      style={{
                        height: '210px',
                        cursor: 'pointer',
                        background: '#030712',
                      }}
                      title="Click to view full image"
                    >
                      <img
                        src={fullSrc}
                        alt={`${item.title} badge proof ${i + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          padding: '6px',
                          transition: 'transform 0.35s ease',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.03)')}
                        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1.0)')}
                      />
                      <div
                        className="position-absolute bottom-0 end-0 p-1 px-2 m-2 rounded small text-white"
                        style={{
                          background: 'rgba(0, 0, 0, 0.7)',
                          fontSize: '0.75rem',
                          backdropFilter: 'blur(4px)',
                        }}
                      >
                        🔍 View Full
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <h2 className="fw-bold text-white mb-2" style={{ fontSize: '1.8rem', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
          {item.title}
        </h2>
        <p className="text-light mb-0" style={{ fontSize: '0.95rem', lineHeight: 1.6, opacity: 0.9 }}>
          {item.description}
        </p>
      </motion.div>
    </div>
  );
}

export default function AwardsMotionPage() {
  const containerRef = useRef(null);
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);

  // Lightbox Modal State
  const [activeModalImg, setActiveModalImg] = useState(null);

  useEffect(() => {
    fetch('https://sanjaydhir.com/api/awards')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch');
        return res.json();
      })
      .then((data) => {
        setAwards(Array.isArray(data) ? data : []);
      })
      .catch((err) => console.error('Error loading awards:', err))
      .finally(() => setLoading(false));
  }, []);

  // Close modal when pressing the Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setActiveModalImg(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  return (
    <section
      ref={containerRef}
      className="position-relative text-light"
      style={{
        minHeight: '260vh',
        background: 'transparent',
      }}
      id="awards-section"
    >
      {/* 1. 3D TROPHY VIEWPORT CANVAS (STICKY LAYER) */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 5,
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <AppleStudioTrophy scrollProgress={scrollYProgress} />
      </div>

      {/* 2. DYNAMIC CONTENT STREAM */}
      <div className="position-relative w-100" style={{ zIndex: 20 }}>
        <div className="container py-5">
          {/* INTRO HERO */}
          <div
            className="d-flex flex-column align-items-center justify-content-center text-center px-3"
            style={{ minHeight: '85vh' }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              style={{ maxWidth: '820px' }}
            >
              <p className="text-warning fw-semibold text-uppercase mb-2" style={{ letterSpacing: '0.12em', fontSize: '0.9rem' }}>
                Academic & Research Distinction
              </p>
              <h1 className="fw-bold text-white mb-4 text-shadow-sm" style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', letterSpacing: '-0.03em', lineHeight: 1.08 }}>
                Engineered for <br /><span className="text-gradient">Strategic Leadership.</span>
              </h1>
              <p className="fs-5 text-light opacity-90 mx-auto" style={{ maxWidth: '640px', lineHeight: 1.6, textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                Pioneering frameworks in Strategic Flexibility, Corporate Governance, and Ambidexterity in Global Emerging Markets.
              </p>
            </motion.div>
          </div>

          {/* LOADING / EMPTY STATES */}
          {loading ? (
            <div className="text-center text-secondary py-5 my-5">Loading accolades...</div>
          ) : awards.length === 0 ? (
            <div className="text-center text-secondary py-5 my-5">No awards published yet.</div>
          ) : (
            awards.map((item, index) => (
              <DynamicAwardCard
                key={item._id}
                item={item}
                index={index}
                onImageClick={(src, title) => setActiveModalImg({ src, title })}
              />
            ))
          )}

          {/* FINAL CLIMAX CARD: TOP 1% GLOBAL SCIENTIST */}
          <div className='my-5'> 
            <p style={{opacity: "0"}}>" "</p>
          </div>
          <div
            className="d-flex flex-column align-items-center justify-content-center text-center px-3 mt-5"
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: false, amount: 0.25 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              style={{
                maxWidth: '750px',
                width: '100%',
                background: 'rgba(7, 10, 19, 0.94)',
                border: '1px solid rgba(245, 158, 11, 0.45)',
                borderRadius: '1.5rem',
                boxShadow: '0 15px 50px rgba(0, 0, 0, 0.9)',
                backdropFilter: 'blur(12px)',
              }}
              className="p-4 p-md-5"
            >
              <span className="badge bg-warning text-dark fw-bold px-3 py-1 mb-2">
                STANFORD & ELSEVIER
              </span>
              <p className="fw-semibold text-warning mb-1" style={{ fontSize: '0.9rem', letterSpacing: '0.08em' }}>
                GLOBAL CITATION INDEX
              </p>
              <h2 className="fw-bold text-white mb-3" style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)', letterSpacing: '-0.02em' }}>
                Top 1% Global Scientist.
              </h2>
              <p className="fs-5 text-light mx-auto mb-4" style={{ maxWidth: '580px', lineHeight: 1.6, opacity: 0.9 }}>
                Ranked globally by Stanford University & Elsevier bibliometrics among the top 1% most-cited researchers in Business & Management.
              </p>

              <div className="d-flex justify-content-center gap-4 gap-md-5 pt-3 border-top border-secondary border-opacity-50">
                <div>
                  <div className="fw-bold fs-3 text-gradient">130+</div>
                  <div className="text-light small opacity-75">Scopus Articles</div>
                </div>
                <div>
                  <div className="fw-bold fs-3 text-warning">2x</div>
                  <div className="text-light small opacity-75">Professor of the Year</div>
                </div>
                <div>
                  <div className="fw-bold fs-3 text-info">IIT Delhi</div>
                  <div className="text-light small opacity-75">Teaching Excellence</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* 3. LIGHTBOX MODAL */}
      <AnimatePresence>
        {activeModalImg && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveModalImg(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(3, 7, 18, 0.88)',
              backdropFilter: 'blur(10px)',
              zIndex: 1060,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px',
              cursor: 'zoom-out',
            }}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setActiveModalImg(null)}
              className="btn btn-outline-light rounded-circle position-absolute top-0 end-0 m-4 d-flex align-items-center justify-content-center"
              style={{ width: '44px', height: '44px', fontSize: '1.25rem', zIndex: 1070 }}
              aria-label="Close image preview"
            >
              &times;
            </button>

            {/* Modal Image Box */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '90vw', maxHeight: '80vh', cursor: 'default' }}
              className="text-center"
            >
              <img
                src={activeModalImg.src}
                alt={activeModalImg.title}
                className="img-fluid rounded-4 shadow-lg border border-secondary border-opacity-50"
                style={{
                  maxHeight: '78vh',
                  maxWidth: '88vw',
                  objectFit: 'contain',
                  boxShadow: '0 25px 60px rgba(0, 0, 0, 0.9)',
                }}
              />
              <p className="mt-3 text-white-50 small mb-0">{activeModalImg.title}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}